import IntlMessageFormat from "intl-messageformat";
import { useLocale } from "./context";

type MessageFormatterStrings = {
    [lang: string]: {
        [key: string]: string;
    };
};
type FormatMessage = (key: string, variables?: { [key: string]: any }) => string;
type CachedFormatter = Map<string, FormatMessage>;
type Cache = {
    [key: string]: IntlMessageFormat;
};

const formatterCache = new Map<MessageFormatterStrings, CachedFormatter>();

/**
 * Handles formatting ICU Message strings to create localized strings for the current locale.
 * Automatically updates when the locale changes, and handles caching of messages for performance.
 * @param strings - A mapping of languages to strings by key.
 */
export function useMessageFormatter(strings: MessageFormatterStrings): FormatMessage {
    const { locale: currentLocale } = useLocale();

    // Check the cache
    let localeCache = formatterCache.get(strings);
    if (localeCache?.has(currentLocale)) {
        return localeCache.get(currentLocale)!;
    }

    // Add to the formatter cache if needed
    if (!localeCache) {
        localeCache = new Map();
        formatterCache.set(strings, localeCache);
    }

    const formatMessage = cacheLocale(currentLocale, strings);
    localeCache.set(currentLocale, formatMessage);
    return formatMessage;
}

/** Get strings associated to the given locale. */
function cacheLocale(locale: string, strings: MessageFormatterStrings): FormatMessage {
    // Create a new message formatter
    const cache: Cache = {};

    const localeStrings = selectLocale(strings, locale);
    return (key, variables) => {
        let message = cache[key + "." + locale];
        if (!message) {
            const msg = localeStrings[key];
            if (!msg) {
                throw new Error(`Could not find intl message ${key} in ${locale} locale`);
            }

            message = new IntlMessageFormat(msg, locale);
            cache[key] = message;
        }
        return message.format(variables);
    };
}

function selectLocale(strings: MessageFormatterStrings, locale: string) {
    // If there is an exact match, use it.
    if (strings[locale]) {
        return strings[locale];
    }

    // Attempt to find the closest match by language.
    // For example, if the locale is fr-CA (French Canadian), but there is only
    // an fr-FR (France) set of strings, use that.
    const language = getLanguage(locale);
    for (const key in strings) {
        if (key.startsWith(language + "-")) {
            return strings[key];
        }
    }

    // Nothing close, use english.
    return strings["en-US"];
}

function getLanguage(locale: string) {
    // @ts-ignore
    if (Intl.Locale) {
        // @ts-ignore
        return new Intl.Locale(locale).language;
    }

    return locale.split("-")[0];
}
